import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import {
  saveR1NetworkRequestSaga,
  saveReturnsFeatureSettingsRequestSaga,
  setR1Network,
  setReturnsFeatureSettings,
} from 'domains/r1Network/R1Network.store';
import { useR1NetworkData } from 'pages/BackOfficePage/BackOfficeCategories/R1Network/R1Network';
import { theme } from 'styles';
import { Box, CleverInput, Flex, InfoAlert, Pipeline, Text, YellowButton } from 'UI';
import { Switch } from 'UI/Switch';

export const SInput = styled(CleverInput)<{ isHighlighted?: boolean }>`
  height: 40px;
  width: 350px;
  border-color: ${({ isHighlighted }) => (isHighlighted ? theme.color.red : null)};
`;

const OpacityWrapper = styled.div<{ $isDisabled?: boolean }>`
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.5 : 1)};
`;

const R1NetworkReturnFeature = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const r1Network = useR1NetworkData();
  const {
    returnFeatureSettings: {
      isRDReturnFeatureEnabledByCountry,
      isMktpReturnFeatureEnabledByCountry,
      isRDReturnFeatureEnabled,
      isMktpReturnFeatureEnabled,
      email,
    },
  } = r1Network;

  const updateFeatureSettings = () => {
    dispatch(saveReturnsFeatureSettingsRequestSaga());
  };

  const setEmail = (newEmail: string | undefined) => {
    dispatch(
      setReturnsFeatureSettings({
        ...r1Network.returnFeatureSettings,
        email: newEmail,
      }),
    );
  };

  const setIsMktpEnabled = (isEnabled: boolean) => {
    if (isMktpReturnFeatureEnabledByCountry) {
      dispatch(
        setReturnsFeatureSettings({
          ...r1Network.returnFeatureSettings,
          isMktpReturnFeatureEnabled: isEnabled,
        }),
      );
      dispatch(saveReturnsFeatureSettingsRequestSaga());
    }
  };

  const setIsRdEnabled = (isEnabled: boolean) => {
    if (isRDReturnFeatureEnabledByCountry) {
      dispatch(
        setReturnsFeatureSettings({
          ...r1Network.returnFeatureSettings,
          isRDReturnFeatureEnabled: isEnabled,
        }),
      );
      dispatch(saveReturnsFeatureSettingsRequestSaga());
    }
  };

  const getDisabledExplanation = () => {
    if (!isRDReturnFeatureEnabledByCountry && !isMktpReturnFeatureEnabledByCountry) {
      return t(
        'backoffice.r1_network.returns_feature.disabled_by_country.both',
        'This feature has been disabled by your Country Admin for both R&D and MKTP products.',
      );
    }
    if (!isMktpReturnFeatureEnabledByCountry) {
      return t(
        'backoffice.r1_network.returns_feature.disabled_by_country.mktp',
        'This feature has been disabled by your Country Admin for MKTP products.',
      );
    }
    return t(
      'backoffice.r1_network.returns_feature.disabled_by_country.rd',
      'This feature has been disabled by your Country Admin for R&D products.',
    );
  };

  return (
    <>
      <Box height={30} />
      <Text type={'h1'}>{t('backoffice.r1_network.returns_feature.title', 'Return feature')}</Text>
      <Text type="base">
        {t(
          'backoffice.r1_network.returns_feature.description',
          'If something is not quite right with ordered parts, hassle-free,' +
            ' you can easily return the items directly trough our Rpartstore. ' +
            'You can activate or disable the feature down below. Make sure to provide a dedicated e-mail address',
        )}
      </Text>
      <Box height={30} />
      {(!isRDReturnFeatureEnabledByCountry || !isMktpReturnFeatureEnabledByCountry) && (
        <InfoAlert size={'md'} message={getDisabledExplanation()} />
      )}
      <OpacityWrapper $isDisabled={!isRDReturnFeatureEnabledByCountry}>
        <Text type="heading3">
          {t('backoffice.r1_network.allow_returns.rd.title', 'Returns for Renault / Dacia products')}
        </Text>
        <Flex justify={'space-between'}>
          <Text type="base">
            {t(
              'backoffice.r1_network.allow_returns.rd.description',
              'The network will be able to return R&D car parts to R1.',
            )}
          </Text>
          <Switch
            checked={isRDReturnFeatureEnabled && isRDReturnFeatureEnabledByCountry}
            onChange={setIsRdEnabled}
            disabled={!isRDReturnFeatureEnabledByCountry}
            data-cy={'return-feature-switch-rd'}
          />
        </Flex>
      </OpacityWrapper>
      <Box height={16} />
      <OpacityWrapper $isDisabled={!isMktpReturnFeatureEnabledByCountry}>
        <Text type="heading3">
          {t('backoffice.r1_network.allow_returns.mktp.title', 'Returns for Marketplace products')}
        </Text>
        <Flex justify={'space-between'}>
          <Text type="base">
            {t(
              'backoffice.r1_network.allow_returns.mktp.description',
              'The network will be able to return MKTP car parts to R1.',
            )}
          </Text>
          <Switch
            checked={isMktpReturnFeatureEnabled && isMktpReturnFeatureEnabledByCountry}
            onChange={setIsMktpEnabled}
            disabled={!isMktpReturnFeatureEnabledByCountry}
            data-cy={'return-feature-switch-mktp'}
          />
        </Flex>
      </OpacityWrapper>
      <Box height={30} />
      <OpacityWrapper $isDisabled={!isRDReturnFeatureEnabledByCountry && !isMktpReturnFeatureEnabledByCountry}>
        <Text type="heading3">{t('backoffice.r1_network.e-mail_address.title', 'e-mail address')}</Text>
      </OpacityWrapper>
      <Box height={16} />
      <OpacityWrapper $isDisabled={!isRDReturnFeatureEnabledByCountry && !isMktpReturnFeatureEnabledByCountry}>
        <Text type="base">
          {t(
            'backoffice.r1_network.e-mail_address.description',
            'Returns from your network will be send to this address.',
          )}
        </Text>
      </OpacityWrapper>
      <Box height={16} />
      <Flex>
        <SInput
          maxLength={256}
          onBlur={(val: string | undefined) => {
            setEmail(val);
          }}
          isHighlighted={
            (isRDReturnFeatureEnabled || isMktpReturnFeatureEnabled) &&
            !email &&
            (isRDReturnFeatureEnabledByCountry || isMktpReturnFeatureEnabledByCountry)
          }
          bordered
          placeholder={t('common.email.address.placeholder', 'Type your e-mail address here')}
          initialValue={email}
          disabled={!isRDReturnFeatureEnabledByCountry && !isMktpReturnFeatureEnabledByCountry}
          data-cy={'return-email-input'}
        />
        <Flex />
        <YellowButton
          onClick={() => {
            updateFeatureSettings();
          }}
          disabled={!isRDReturnFeatureEnabledByCountry && !isMktpReturnFeatureEnabledByCountry}
          data-cy={'returns-save-button'}
        >
          {t('common.action.save', 'Save')}
        </YellowButton>
      </Flex>
      <Box height={30} />
      <Pipeline size={'100%'} horizontal />
      <Box height={30} />
      <Text type="heading3">{t('backoffice.r1_network.dealer_pickup.title', 'Dealer pick-up')}</Text>
      <Box height={16} />
      <Flex justify={'space-between'}>
        <Text type="base">
          {t(
            'backoffice.r1_network.dealer_pickup.description',
            'Allow my network to pick-up their order in my garage.',
          )}
        </Text>
        <Switch
          checked={r1Network.isDealerPickupEnabled}
          onChange={() => {
            const enabled = !r1Network.isDealerPickupEnabled;
            dispatch(setR1Network(enabled));
            dispatch(saveR1NetworkRequestSaga({ isDealerPickupEnabled: enabled }));
          }}
          data-cy={'dealer-pickup-switch'}
        />
      </Flex>
    </>
  );
};

export default R1NetworkReturnFeature;
